import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Transfer, DirectUpdateReason } from '../../models';
import { MatInventoryService } from '../../services/mat-inventory.service';

@Component({
  selector: 'app-confirm-transfers-dialog',
  templateUrl: './confirm-transfers-dialog.component.html',
  styleUrls: ['./confirm-transfers-dialog.component.scss']
})
export class ConfirmTransfersDialogComponent implements OnInit {
  @Input() transfers: Transfer[] = [];
  @Input() reasons: DirectUpdateReason[] = []; // Optional input for DirectUpdateReasons
  /** 'transfer' = inter-hub transfer, 'direct-update' = add/adjust inventory at a location */
  @Input() mode: 'transfer' | 'direct-update' = 'transfer';
  /** Hint for which reason codes to show: 'add', 'remove', or 'mixed' (both).
   *  Only used when mode === 'direct-update'. */
  @Input() actionType: 'add' | 'remove' | 'mixed' = 'mixed';
  /** Parent-owned busy flag — stays true while the parent's confirm API call is in flight so the spinner keeps showing
   *  and both buttons stay disabled, preventing double-submit even if the parent hasn't closed the dialog yet. */
  @Input() isBusy = false;
  @Output() confirmed = new EventEmitter<any>();
  @Output() cancelled = new EventEmitter<void>();

  confirmForm: FormGroup;
  directUpdateReasons: DirectUpdateReason[] = [];
  isSubmitting = false;
  errorMessage: string | null = null;

  /** Hardcoded reason options for direct-update Add actions */
  private addReasons: DirectUpdateReason[] = [
    { ReferenceCodeSetId: 0, Code: 'NewProcurement', Description: 'New Procurement' },
  ];
  /** Hardcoded reason options for direct-update Remove actions */
  private removeReasons: DirectUpdateReason[] = [
    { ReferenceCodeSetId: 0, Code: 'Repaired', Description: 'Repaired' },
    { ReferenceCodeSetId: 0, Code: 'Lost', Description: 'Lost' },
    { ReferenceCodeSetId: 0, Code: 'Discard', Description: 'Discard' },
  ];

  // Expose data for template (matching Material dialog pattern)
  get data() {
    return { transfers: this.transfers };
  }

  constructor(
    private fb: FormBuilder,
    private inventoryService: MatInventoryService
  ) {
    this.confirmForm = this.fb.group({
      reasonCode: ['', Validators.required],
      reasonDetails: ['']
    });
  }

  ngOnInit(): void {
    // For transfer mode, reason code is not needed — remove the required validator
    if (this.mode === 'transfer') {
      this.confirmForm.get('reasonCode')?.clearValidators();
      this.confirmForm.get('reasonCode')?.updateValueAndValidity();
    }

    if (this.mode === 'direct-update') {
      // Use hardcoded context-appropriate reasons based on action type
      if (this.actionType === 'add') {
        this.directUpdateReasons = this.addReasons;
      } else if (this.actionType === 'remove') {
        this.directUpdateReasons = this.removeReasons;
      } else {
        // Mixed: show all
        this.directUpdateReasons = [...this.addReasons, ...this.removeReasons];
      }
    } else if (this.reasons && this.reasons.length > 0) {
      this.directUpdateReasons = this.reasons;
    } else {
      this.loadDirectUpdateReasons();
    }
  }

  loadDirectUpdateReasons(): void {
    this.inventoryService.summaryResponse$.subscribe({
      next: (response) => {
        if (response && response.DirectUpdateReasons) {
          this.directUpdateReasons = response.DirectUpdateReasons;
        }
      },
      error: (error) => {
        console.error('Failed to load direct update reasons:', error);
      }
    });
  }

  onCancel(): void {
    this.cancelled.emit();
  }

  onConfirm(): void {
    // Guard against rapid double-clicks: bail if a submit is already in flight or the parent is still processing.
    if (this.isSubmitting || this.isBusy) return;
    if (this.confirmForm.invalid) {
      this.confirmForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = null;

    const reasonCode = this.confirmForm.value.reasonCode;
    const reasonDetails = this.confirmForm.value.reasonDetails || undefined;

    // Emit the reason code directly for the parent component to handle
    this.confirmed.emit({ reasonCode, reasonDetails });
    // Let the parent's `isBusy` take over on the next tick. If the parent doesn't
    // set one (dialog closes immediately on emit), releasing the local guard is safe
    // — the component will be destroyed before another click can land.
    setTimeout(() => { this.isSubmitting = false; }, 0);
  }

  /** True whenever the button should show the spinner and stay disabled. */
  get isProcessing(): boolean {
    return this.isSubmitting || this.isBusy;
  }

  get totalTransfers(): number {
    return this.transfers.length;
  }

  get totalQuantity(): number {
    return this.transfers.reduce((sum, t) => sum + t.qty, 0);
  }

  /** Context-aware labels */
  get dialogTitle(): string {
    return this.mode === 'direct-update' ? 'Confirm Inventory Update' : 'Confirm Transfers';
  }
  get dialogSubtitle(): string {
    return this.mode === 'direct-update'
      ? 'Review the inventory changes and provide reason details'
      : 'Review your transfers and provide reason details';
  }
  get summaryTitle(): string {
    return this.mode === 'direct-update' ? 'Update Summary' : 'Transfers Summary';
  }
  get itemLabel(): string {
    return this.mode === 'direct-update' ? 'Update' : 'Transfer';
  }
  get confirmButtonLabel(): string {
    return this.mode === 'direct-update' ? 'Confirm Update' : 'Confirm Transfers';
  }
  get confirmingLabel(): string {
    return this.mode === 'direct-update' ? 'Updating...' : 'Confirming...';
  }
}

